import { icon } from '@fortawesome/fontawesome-svg-core/import.macro';
import Box from '@mui/material/Box';
import { Theme, useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { memo } from 'react';
import { Icon } from 'src/components/Icon';
import { IconButton } from 'src/components/IconButton';
import { useOptionalSession } from 'src/contexts/SessionContext';

import { ActionsMenu } from './ActionsMenu';

export interface TopBarProps {
  toggleOpenNav: () => void;
  isAdmin?: boolean;
  bgcolor: string;
}

export const TopBar: React.FC<TopBarProps> = memo(
  ({ toggleOpenNav, isAdmin, bgcolor }) => {
    const { session, isDarkmode } = useOptionalSession();
    const darkMode = isDarkmode();
    const theme = useTheme();
    const xlDown = useMediaQuery((theme: Theme) =>
      theme.breakpoints.down('xl')
    );

    const adminLogo = darkMode
      ? 'https://res.cloudinary.com/brandpad/image/upload/c_scale,dpr_auto,f_auto,w_2816/v1/13578/colorprism-textlight-h'
      : 'https://res.cloudinary.com/brandpad/image/upload/c_scale,dpr_auto,f_auto,w_2816/v1/13578/colorprism-textdark-h';

    const regularLogo = darkMode ? session?.darkmodeLogoUrl : session?.logoUrl;

    const logoUrl = isAdmin ? adminLogo : regularLogo;

    // We need to take a bgcolor string like "background.level1"
    // and turn it into an actual palette variable by splitting it
    // so we can find the matching channel variable
    const bgcolorSegments = bgcolor?.split('.');
    const bgVar =
      bgcolorSegments.length === 2 &&
      theme.vars.palette[bgcolorSegments[0]] &&
      theme.vars.palette[bgcolorSegments[0]][bgcolorSegments[1] + 'Channel'];

    return (
      <Box
        sx={{
          position: 'sticky',
          top: '0px',
          height: '56px',
          borderBottom: 1,
          borderColor: 'divider',
          backgroundColor: (theme) =>
            bgVar ||
            `rgba(${theme.vars.palette.background.defaultChannel} / 0.7)`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          zIndex: theme.zIndex.appBar,
          backdropFilter: 'blur(10px)',
          transition: 'background-color 0.5s',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton onClick={toggleOpenNav} sx={{ mr: 2 }}>
            <Icon
              color="action"
              size="medium"
              icon={icon({ name: 'bars', style: 'regular' })}
            />
          </IconButton>
          {xlDown ? (
            <Box
              component="img"
              src={logoUrl}
              sx={{
                maxWidth: '100%',
                maxHeight: '38px',
                height: 'auto',
                width: '100%',
                objectFit: 'contain',
                objectPosition: 'left center',
                my: 1,
              }}
            />
          ) : (
            <Box />
          )}
        </Box>
        {!isAdmin && <ActionsMenu />}
      </Box>
    );
  }
);

TopBar.displayName = 'TopBar';
